import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks'
import { SectionList, SectionListRenderItem, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native'
import { Separator, TextList, ItemPress, Right, SlaveIcon } from '../components/List'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import Icon from '../components/Icon'
import { t, ngettext, msgid } from 'ttag'
import { HeaderButtons, HeaderButton, Item } from 'react-navigation-header-buttons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import {
  getSlave,
  getAllSlaves,
  formatSlaveType,
  formatSlaveStatus,
  slaveTypeList,
  slaveStatusList,
  selectSlavesByTypeForSectionList,
  selectSlavesByAmbientForSectionList,
  selectSlaveSummary
} from '#app/slave'
import type { SlaveType, SlaveStatus } from '#app/slave'
import { useAppTheme } from '#app/theme'
import { useNavigation } from '@react-navigation/native'
import SegmentedControl from '@react-native-segmented-control/segmented-control'
import { colors } from '#app/theme/colors'

const SlaveList = () => {
  const theme = useAppTheme()
  const slavesByAmbient = useAppSelector(selectSlavesByAmbientForSectionList)
  const slavesByType = useAppSelector(selectSlavesByTypeForSectionList)
  const allSlaves = useAppSelector(getAllSlaves)
  const loading = useAppSelector(state => state.slaves.loading)

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<SlaveType | null>(null)
  const [statusFilter, setStatusFilter] = useState<SlaveStatus | null>(null)

  const dispatch = useAppDispatch()
  const navigation = useNavigation()
  const insets = useSafeAreaInsets()

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderButtons HeaderButtonComponent={HeaderIconButton}>
          <Item
            title={t`Add`}
            iconName="add"
            onPress={() => navigation.navigate('NewSlave')}
          />
        </HeaderButtons>
      )
    })
  }, [navigation])

  const onRefresh = useCallback(() => {
    dispatch({ type: 'DATA_GET_SLAVES_REQUEST' })
  }, [dispatch])

  const hasFilters = search.length > 0 || typeFilter != null || statusFilter != null

  const matchingIds = useMemo(() => {
    if (!hasFilters) return null
    const lowerSearch = search.toLowerCase()
    const ids = new Set<number>()
    for (const slave of allSlaves) {
      if (typeFilter != null && slave.type !== typeFilter) continue
      if (statusFilter != null && slave.status !== statusFilter) continue
      if (lowerSearch && !slave.name.toLowerCase().includes(lowerSearch) && !slave.code.toLowerCase().includes(lowerSearch)) continue
      ids.add(slave.id)
    }
    return ids
  }, [allSlaves, search, typeFilter, statusFilter, hasFilters])

  const sections = selectedIndex !== 0 ? slavesByType : slavesByAmbient

  const filteredSections = useMemo(() => {
    if (matchingIds == null) return sections
    return sections
      .map(section => ({
        ...section,
        data: section.data.filter((item: { id: number }) => matchingIds.has(item.id))
      }))
      .filter(section => section.data.length > 0)
  }, [sections, matchingIds])

  const clearFilters = useCallback(() => {
    setSearch('')
    setTypeFilter(null)
    setStatusFilter(null)
  }, [])

  return (
    <View style={{ maxHeight: '100%', flexBasis: 1, flexGrow: 1 }}>
      <SectionList
        ListHeaderComponent={
          <View style={s.headerContainer}>
            <View style={s.searchContainer}>
              <MaterialCommunityIcons name="magnify" size={20} color={theme.formForm.searchIconColor} style={s.searchIcon} />
              <TextInput
                style={[s.searchInput, { backgroundColor: theme.formForm.search, color: theme.textColor }]}
                placeholder={t`Search devices...`}
                placeholderTextColor={theme.formForm.placeholderTextColor}
                value={search}
                onChangeText={setSearch}
                autoCorrect={false}
                autoCapitalize="none"
                clearButtonMode="while-editing"
              />
            </View>

            <View style={s.filtersRow}>
              <Text style={[s.filterLabel, { color: theme.textColor70 }]}>{t`Type`}:</Text>
              <FilterChip
                label={t`All`}
                selected={typeFilter == null}
                onPress={() => setTypeFilter(null)}
              />
              {slaveTypeList.map(st => (
                <FilterChip
                  key={st}
                  label={formatSlaveType(st).replace('myio ', '')}
                  selected={typeFilter === st}
                  onPress={() => setTypeFilter(typeFilter === st ? null : st)}
                />
              ))}
            </View>

            <View style={s.filtersRow}>
              <Text style={[s.filterLabel, { color: theme.textColor70 }]}>{t`Status`}:</Text>
              <FilterChip
                label={t`All`}
                selected={statusFilter == null}
                onPress={() => setStatusFilter(null)}
              />
              {slaveStatusList.map(ss => (
                <FilterChip
                  key={ss}
                  label={formatSlaveStatus(ss)}
                  selected={statusFilter === ss}
                  onPress={() => setStatusFilter(statusFilter === ss ? null : ss)}
                  dotColor={ss === 'online' ? colors.green : ss === 'offline' ? '#999' : '#f0ad4e'}
                />
              ))}
            </View>

            {hasFilters && (
              <View style={s.activeFiltersRow}>
                <Text style={[s.resultCount, { color: theme.textColor70 }]}>
                  {matchingIds != null
                    ? ngettext(msgid`${matchingIds.size} result`, `${matchingIds.size} results`, matchingIds.size)
                    : ''}
                </Text>
                <TouchableOpacity onPress={clearFilters} style={s.clearButton}>
                  <MaterialCommunityIcons name="close-circle" size={14} color={theme.colors.grey} />
                  <Text style={[s.clearText, { color: theme.colors.grey }]}>{t`Clear filters`}</Text>
                </TouchableOpacity>
              </View>
            )}

            <SegmentedControl
              values={[t`Ambient`, t`Type`]}
              selectedIndex={selectedIndex}
              onChange={(event) => setSelectedIndex(event.nativeEvent.selectedSegmentIndex)}
              style={s.segmentedControl}
            />
          </View>
        }
        renderSectionHeader={({ section: { title, data } }) => {
          const n = data.length
          return <Separator text={title} detail={ngettext(msgid`${n} product`, `${n} products`, n)} />
        }}
        renderItem={renderItem}
        keyExtractor={(item) => item.key}
        sections={filteredSections}
        contentContainerStyle={{ paddingBottom: insets.bottom }}
        refreshing={loading}
        onRefresh={onRefresh}
        windowSize={5}
        maxToRenderPerBatch={10}
        initialNumToRender={15}
        ListFooterComponent={ListFooterComponent}
        ListEmptyComponent={
          hasFilters
            ? <View style={s.emptyContainer}>
                <MaterialCommunityIcons name="magnify-close" size={48} color={theme.textColor50} />
                <Text style={[s.emptyText, { color: theme.textColor70 }]}>{t`No devices match your filters`}</Text>
              </View>
            : null
        }
      />
    </View>
  )
}

export default SlaveList

const FilterChip = ({ label, selected, onPress, dotColor }: {
  label: string
  selected: boolean
  onPress: () => void
  dotColor?: string
}) => (
  <TouchableOpacity
    onPress={onPress}
    style={[
      s.chip,
      selected && s.chipSelected
    ]}
    activeOpacity={0.7}
  >
    {dotColor != null && <View style={[s.statusDot, { backgroundColor: dotColor }]} />}
    <Text style={[s.chipText, selected && s.chipTextSelected]}>{label}</Text>
  </TouchableOpacity>
)

const HeaderIconButton = (passMeFurther: any) => <HeaderButton {...passMeFurther} IconComponent={Icon} iconSize={28} />

const formatTotal = (n: number) => ngettext(msgid`${n} product paired.`, `${n} products paired.`, n)
const formatOffline = (n: number) => ngettext(msgid`${n} product is offline.`, `${n} products are offline.`, n)
const formatBad = (n: number) => ngettext(msgid`${n} product has weak connectivity.`, `${n} products have weak connectivity.`, n)

const ListFooterComponent = () => {
  const theme = useAppTheme()
  const { total, offline, bad } = useAppSelector(selectSlaveSummary)
  const text = [
    formatTotal(total),
    ...(offline > 0
      ? [formatOffline(offline)]
      : []),
    ...(bad > 0
      ? ['\n', formatBad(bad)]
      : [])
  ].join(' ')

  return (
    <View style={[s.footerContainer, { backgroundColor: theme.formForm.search }]}>
      <View style={s.footerRow}>
        <View style={s.footerStat}>
          <Text style={[s.footerNumber, { color: theme.textColor }]}>{total}</Text>
          <Text style={[s.footerLabel, { color: theme.textColor70 }]}>{t`paired`}</Text>
        </View>
        {offline > 0 && (
          <View style={s.footerStat}>
            <Text style={[s.footerNumber, { color: theme.colors.danger }]}>{offline}</Text>
            <Text style={[s.footerLabel, { color: theme.textColor70 }]}>{t`offline`}</Text>
          </View>
        )}
        {bad > 0 && (
          <View style={s.footerStat}>
            <Text style={[s.footerNumber, { color: '#f0ad4e' }]}>{bad}</Text>
            <Text style={[s.footerLabel, { color: theme.textColor70 }]}>{t`weak signal`}</Text>
          </View>
        )}
      </View>
    </View>
  )
}

const ListItem = React.memo(({ id }: ListItemProps) => {
  const navigation = useNavigation()
  const theme = useAppTheme()
  const { type, name, status } = useAppSelector(state => getSlave(state, id))
  const setSlaveId = useCallback(() => {
    navigation.navigate('SlaveDetail', { id: String(id) })
  }, [navigation, id])

  return (
    <ItemPress onPress={setSlaveId}>
      <SlaveIcon type={type}/>
      <View style={s.itemContent}>
        <TextList text={name}/>
        <Text style={[s.itemStatus, {
          color: status === 'online' ? colors.green : status === 'bad' ? '#f0ad4e' : '#999'
        }]}>
          {formatSlaveStatus(status)}
        </Text>
      </View>
      <Right>
        <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey}/>
      </Right>
    </ItemPress>
  )
})

const renderItem: SectionListRenderItem<ItemData> = ({ item }) => <ListItem id={item.id} />

interface ItemData {
  key: string
  id: number
}
type ListItemProps = Omit<ItemData, 'key'>

const s = StyleSheet.create({
  headerContainer: {
    paddingHorizontal: 15,
    paddingTop: 8,
    paddingBottom: 4
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    zIndex: 1
  },
  searchInput: {
    flex: 1,
    height: 40,
    borderRadius: 10,
    paddingLeft: 40,
    paddingRight: 12,
    fontSize: 15
  },
  filtersRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 8,
    gap: 6
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginRight: 2
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 16,
    backgroundColor: 'rgba(128, 128, 128, 0.12)',
    gap: 4
  },
  chipSelected: {
    backgroundColor: colors.purple,
  },
  chipText: {
    fontSize: 12,
    color: '#666'
  },
  chipTextSelected: {
    color: '#fff',
    fontWeight: '600'
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 4
  },
  activeFiltersRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8
  },
  resultCount: {
    fontSize: 12
  },
  clearButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4
  },
  clearText: {
    fontSize: 12
  },
  segmentedControl: {
    marginBottom: 4
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
    gap: 12
  },
  emptyText: {
    fontSize: 15
  },
  itemContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginRight: 8
  },
  itemStatus: {
    fontSize: 11,
    fontWeight: '500'
  },
  footerContainer: {
    margin: 15,
    borderRadius: 10,
    padding: 16
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-around'
  },
  footerStat: {
    alignItems: 'center'
  },
  footerNumber: {
    fontSize: 22,
    fontWeight: '700'
  },
  footerLabel: {
    fontSize: 12,
    marginTop: 2
  },
  summaryText: {
    fontWeight: 'bold',
    textAlign: 'center'
  }
})
